import React from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { CContainer, CCard, CCardBody, CButton } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilArrowLeft } from '@coreui/icons'
import LoadingSpinner from './LoadingSpinner'
import { usePermission } from '../../contexts/PermissionContext'

const ProtectedRoute = ({
  children,
  requireAuth = true,
  requireOnboarding = false,
  allowedRoles = null,
  requiredPermission = null, // { resource: 'courses', action: 'read' } or { viewCode: 'courses', actionCode: 'view' }
  minHierarchyLevel = null
}) => {
  const location = useLocation()
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth)
  const { hasPermission, hasRole, loading: permissionsLoading } = usePermission()
  
  // Helper to get hierarchy level
  const getHierarchyLevel = () => {
    return user?.Role?.hierarchy_level || 0
  }

  // Show loading spinner while checking authentication or permissions
  if (loading || permissionsLoading) {
    return <LoadingSpinner />
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user exists
  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user needs onboarding (first login not completed)
  if (requireAuth && user && !user.first_login_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  // Check onboarding requirement
  if (requireOnboarding && user?.requiresOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  // Check role-based access
  if (allowedRoles && user) {
    const userRole = user.role || user.Role?.name
    if (!allowedRoles.includes(userRole)) {
      return <AccessDenied message="Your role doesn't have access to this page." />
    }
  }

  // Check hierarchy level requirement
  if (minHierarchyLevel !== null) {
    const userLevel = getHierarchyLevel()
    console.log("userLevel", userLevel, minHierarchyLevel)
    if (userLevel < minHierarchyLevel) {
      return <AccessDenied message="You don't have sufficient privileges to access this page." />
    }
  }

  // Check specific permission requirement
  if (requiredPermission) {
    const { resource, action, viewCode, actionCode } = requiredPermission
    // Support both old (resource/action) and new (viewCode/actionCode) formats
    const view = viewCode || resource
    const act = actionCode || action
    
    if (view && act && !hasPermission(view, act)) {
      return <AccessDenied message={`You don't have permission to ${act} ${view}.`} />
    }
  }

  return children
}

// Access Denied Component
const AccessDenied = ({ message }) => {
  const navigate = useLocation()

  return (
    <CContainer className="min-vh-100 d-flex align-items-center justify-content-center">
      <CCard className="text-center" style={{ maxWidth: '500px' }}>
        <CCardBody className="p-5">
          <div className="mb-4">
            <CIcon icon={cilLockLocked} size="4xl" className="text-danger" />
          </div>
          <h3 className="text-danger mb-3">Access Denied</h3>
          <p className="text-muted mb-4">{message}</p>
          <CButton
            color="primary"
            onClick={() => window.history.back()}
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            Go Back
          </CButton>
        </CCardBody>
      </CCard>
    </CContainer>
  )
}

export default ProtectedRoute